import { createApiClient } from './client'

export interface PhotoLike {
  id: string
  photoId: string
  userId: string
  userName: string
  userAvatar?: string
  createdAt: string
}

export interface PhotoComment {
  id: string
  photoId: string
  userId: string
  userName: string
  userAvatar?: string
  comment: string
  createdAt: string
  updatedAt: string
}

export interface PhotoLikesResponse {
  likes: PhotoLike[]
  count: number
  hasMore: boolean
}

export interface PhotoCommentsResponse {
  comments: PhotoComment[]
  count: number
  hasMore: boolean
}

export class PhotoInteractionsApi {
  private apiClient: ReturnType<typeof createApiClient>

  private unwrapResponse<T>(response: any): T {
    if (response && typeof response === 'object' && 'success' in response && 'data' in response) {
      return response.data as T
    }
    return response as T
  }

  constructor(getAuthToken: () => Promise<string | null>) {
    this.apiClient = createApiClient(getAuthToken)
  }

  // Like methods
  async likePhoto(photoId: string): Promise<void> {
    try {
      await this.apiClient.post(`/photos/${photoId}/likes`)
    } catch (err) {
      throw err
    }
  }

  async unlikePhoto(photoId: string): Promise<void> {
    try {
      await this.apiClient.delete(`/photos/${photoId}/likes`)
    } catch (err) {
      throw err
    }
  }

  async getPhotoLikes(photoId: string, page: number = 1, limit: number = 20): Promise<PhotoLikesResponse> {
    try {
      const raw = await this.apiClient.get<any>(`/photos/${photoId}/likes`, {
        params: { page, limit }
      })
      return this.unwrapResponse<PhotoLikesResponse>(raw)
    } catch (err) {
      throw err
    }
  }

  async getLikeCount(photoId: string): Promise<number> {
    try {
      const raw = await this.apiClient.get<any>(`/photos/${photoId}/likes/count`)
      const unwrapped = this.unwrapResponse<{ count: number }>(raw)
      return unwrapped.count || 0
    } catch (err) {
      throw err
    }
  }

  async hasUserLiked(photoId: string): Promise<boolean> {
    try {
      const raw = await this.apiClient.get<any>(`/photos/${photoId}/likes/me`)
      const unwrapped = this.unwrapResponse<{ hasLiked: boolean }>(raw)
      return unwrapped.hasLiked || false
    } catch (err) {
      throw err
    }
  }

  // Comment methods
  async addComment(photoId: string, comment: string): Promise<PhotoComment> {
    try {
      const raw = await this.apiClient.post<any>(`/photos/${photoId}/comments`, {
        comment
      })
      const unwrapped = this.unwrapResponse<{ comment: PhotoComment }>(raw)
      return unwrapped.comment
    } catch (err) {
      throw err
    }
  }

  async getComments(photoId: string, page: number = 1, limit: number = 10): Promise<PhotoCommentsResponse> {
    try {
      const raw = await this.apiClient.get<any>(`/photos/${photoId}/comments`, {
        params: { page, limit }
      })
      return this.unwrapResponse<PhotoCommentsResponse>(raw)
    } catch (err) {
      throw err
    }
  }

  async updateComment(photoId: string, commentId: string, comment: string): Promise<PhotoComment> {
    try {
      const raw = await this.apiClient.put<any>(`/photos/${photoId}/comments/${commentId}`, {
        comment
      })
      const unwrapped = this.unwrapResponse<{ comment: PhotoComment }>(raw)
      return unwrapped.comment
    } catch (err) {
      throw err
    }
  }

  async deleteComment(photoId: string, commentId: string): Promise<void> {
    try {
      await this.apiClient.delete(`/photos/${photoId}/comments/${commentId}`)
    } catch (err) {
      throw err
    }
  }

  async getCommentCount(photoId: string): Promise<number> {
    try {
      const raw = await this.apiClient.get<any>(`/photos/${photoId}/comments/count`)
      const unwrapped = this.unwrapResponse<{ count: number }>(raw)
      return unwrapped.count || 0
    } catch (err) {
      throw err
    }
  }
}
